import { useState, useEffect, useCallback, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { ResizableTableHead, useResizableColumns } from "@/components/ui/resizable-table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Tooltip as STooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import { Loader2, Star, Trash2, Edit2, Eye, Zap, Plus, FileText, ExternalLink, Image as ImageIcon, RefreshCw, Upload } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

function Tip({ label, children }) {
  return (
    <STooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent className="bg-[#2F4F4F] text-white text-[11px] px-2.5 py-1 rounded-md">{label}</TooltipContent>
    </STooltip>
  );
}

const inputCls = "border-[#BFBFBF] bg-[#FBF9F5] focus:border-[#2F4F4F] focus:ring-[#2F4F4F]/10 rounded-lg text-sm";

export default function BlogPanel() {
  const [articles, setArticles] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generatingCovers, setGeneratingCovers] = useState(false);
  const [filterStatus, setFilterStatus] = useState("");
  const [editDialog, setEditDialog] = useState(null);
  const [createDialog, setCreateDialog] = useState(false);
  const [pillarConfirmOpen, setPillarConfirmOpen] = useState(false);
  const [activeJob, setActiveJob] = useState(null);

  const cols = useResizableColumns("blog-table", {
    fav: 40,
    image: 70,
    titre: 320,
    slug: 200,
    date: 110,
    statut: 110,
    actions: 160,
  });

  const loadArticles = useCallback(async () => {
    setLoading(true);
    try {
      let url = `/admin/blog/articles?page=${page}&limit=25`;
      if (filterStatus) url += `&status=${filterStatus}`;
      const { data } = await api.get(url);
      setArticles(data.articles || []);
      setTotal(data.total || 0);
      setPages(data.pages || 1);
    } catch { toast.error("Erreur chargement articles"); }
    setLoading(false);
  }, [page, filterStatus]);

  const loadStats = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/blog/stats");
      setStats(data);
    } catch {}
  }, []);

  useEffect(() => { loadArticles(); }, [loadArticles]);
  useEffect(() => { loadStats(); }, [loadStats]);

  // Poll active job status every 10s — keeps UI in sync while background generation runs.
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await api.get("/admin/blog/jobs/active");
        if (cancelled) return;
        setActiveJob(data.job || null);
        // When a job finishes, refresh articles list once.
        if (!data.job && activeJob) {
          loadArticles();
          loadStats();
        }
      } catch { /* silent */ }
    };
    tick();
    const interval = setInterval(tick, 10000);
    return () => { cancelled = true; clearInterval(interval); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGenerate = async (pillar = false, reset = false) => {
    setGenerating(true);
    try {
      const { data } = await api.post("/admin/blog/generate", { count: pillar ? 5 : 3, pillar, reset });
      if (data.queued) {
        toast.success(data.message || "Génération démarrée en arrière-plan.");
        // Trigger immediate poll
        const { data: aj } = await api.get("/admin/blog/jobs/active");
        setActiveJob(aj.job || null);
      } else {
        toast.success(`${data.generated || 0} article(s) généré(s)`);
        loadArticles();
        loadStats();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de la génération");
    }
    setGenerating(false);
  };

  const confirmAndGeneratePillars = () => {
    // Reset = true : supprime les piliers existants puis régénère
    setPillarConfirmOpen(false);
    handleGenerate(true, true);
  };

  const handleGenerateCovers = async () => {
    setGeneratingCovers(true);
    try {
      const { data } = await api.post("/admin/blog/generate-missing-covers");
      if (data.queued > 0) {
        toast.success(`${data.queued} image(s) en cours de génération — rafraîchissement automatique…`);
        // Poll every 15s for up to 3 minutes
        let attempts = 0;
        const poll = setInterval(async () => {
          attempts++;
          await loadArticles();
          await loadStats();
          if (attempts >= 12) clearInterval(poll);
        }, 15000);
      } else {
        toast.info("Aucune image manquante");
      }
    } catch {
      toast.error("Erreur lors du lancement");
    }
    setGeneratingCovers(false);
  };

  const handleRegenerateCover = async (article) => {
    try {
      await api.post(`/admin/blog/articles/${article.id}/regenerate-cover`);
      toast.success("Image en cours de génération (30-60s)");
      setTimeout(() => { loadArticles(); loadStats(); }, 30000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const handleToggleFeatured = async (article) => {
    try {
      const { data } = await api.put(`/admin/blog/articles/${article.id}/toggle-featured`);
      toast.success(data.message);
      loadArticles();
      loadStats();
    } catch { toast.error("Erreur"); }
  };

  const handleDelete = async (article) => {
    try {
      await api.delete(`/admin/blog/articles/${article.id}`);
      toast.success("Article supprimé");
      loadArticles();
      loadStats();
    } catch { toast.error("Erreur suppression"); }
  };

  const handleStatusToggle = async (article) => {
    const newStatus = article.status === "published" ? "draft" : "published";
    try {
      await api.put(`/admin/blog/articles/${article.id}`, { status: newStatus });
      toast.success(newStatus === "published" ? "Article publié" : "Article dépublié");
      loadArticles();
      loadStats();
    } catch { toast.error("Erreur"); }
  };

  return (
    <TooltipProvider delayDuration={200}>
      <div className="space-y-5">
        <div className="flex justify-between items-center flex-wrap gap-3">
          <div>
            <h3 className="text-lg font-medium text-[#2F4F4F] font-montserrat tracking-tight">Blog SEO</h3>
            <p className="text-xs text-[#6B6B6B] mt-0.5">Articles auto-générés — Publication <strong>lun &amp; jeu à 3h</strong> (heure de Paris) • Image générée 15 min après le texte</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={() => setCreateDialog(true)} className="rounded-full border-[#BFBFBF] text-[#6B6B6B]">
              <Plus className="w-4 h-4 mr-1" /> Écrire
            </Button>
            {(stats.missing_cover || 0) > 0 && (
              <Button size="sm" onClick={handleGenerateCovers} disabled={generatingCovers || !!activeJob} className="bg-purple-600 text-white hover:bg-purple-700 rounded-full" data-testid="generate-missing-covers-btn">
                {generatingCovers ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <ImageIcon className="w-4 h-4 mr-1" />}
                Images manquantes ({stats.missing_cover})
              </Button>
            )}
            <Button size="sm" onClick={() => setPillarConfirmOpen(true)} disabled={generating || !!activeJob} className="bg-[#C46A4A] text-white hover:bg-[#A6553A] rounded-full disabled:opacity-50" data-testid="generate-pillars-btn">
              {generating ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Star className="w-4 h-4 mr-1" />}
              Articles piliers
            </Button>
            <Button size="sm" onClick={() => handleGenerate(false)} disabled={generating || !!activeJob} className="bg-[#2F4F4F] text-white hover:bg-[#1F3535] rounded-full disabled:opacity-50">
              {generating ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Zap className="w-4 h-4 mr-1" />}
              Générer
            </Button>
          </div>
        </div>

        {/* Active job banner */}
        {activeJob && (
          <div className="flex items-center gap-3 p-4 rounded-xl border border-[#D4AF37]/40 bg-[#FBF9F5]" data-testid="active-job-banner">
            <Loader2 className="w-5 h-5 animate-spin text-[#C46A4A] flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-[#1F3535]">Génération en cours en arrière-plan</p>
              <p className="text-xs text-[#6B6B6B] truncate mt-0.5">
                {activeJob.phase || "Préparation…"} — {activeJob.generated_count || 0}/{activeJob.count_planned || activeJob.count_requested} articles publiés • Vous pouvez continuer à utiliser le site, les images de couverture seront créées automatiquement 15 min après chaque article.
              </p>
            </div>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: "Total", value: stats.total || 0, color: "text-[#2F4F4F]" },
            { label: "Publiés", value: stats.published || 0, color: "text-green-600" },
            { label: "Brouillons", value: stats.draft || 0, color: "text-amber-600" },
            { label: "Favoris", value: stats.featured || 0, color: "text-amber-500" },
          ].map(s => (
            <div key={s.label} className="bg-white border border-[#BFBFBF] rounded-lg p-3 text-center">
              <p className={`text-xl font-bold ${s.color}`} style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{s.value}</p>
              <p className="text-[10px] text-[#6B6B6B] uppercase tracking-wider mt-0.5">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Filter */}
        <div className="flex gap-2">
          <Select value={filterStatus || "all"} onValueChange={(v) => { setFilterStatus(v === "all" ? "" : v); setPage(1); }}>
            <SelectTrigger className="h-8 text-xs border-[#BFBFBF] bg-white rounded-full w-[140px]"><SelectValue placeholder="Statut" /></SelectTrigger>
            <SelectContent className="bg-white border-[#BFBFBF]">
              <SelectItem value="all">Tous</SelectItem>
              <SelectItem value="published">Publiés</SelectItem>
              <SelectItem value="draft">Brouillons</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        <div className="bg-white border border-[#BFBFBF] rounded-xl overflow-hidden">
          <Table className="table-fixed">
            <TableHeader>
              <TableRow className="bg-[#FBF9F5]/50">
                <ResizableTableHead id="fav" width={cols.widths.fav} onWidthChange={(w) => cols.setWidth("fav", w)} className="text-[#6B6B6B] text-xs"></ResizableTableHead>
                <ResizableTableHead id="image" width={cols.widths.image} onWidthChange={(w) => cols.setWidth("image", w)} className="text-[#6B6B6B] text-xs">Image</ResizableTableHead>
                <ResizableTableHead id="titre" width={cols.widths.titre} onWidthChange={(w) => cols.setWidth("titre", w)} className="text-[#6B6B6B] text-xs">Titre</ResizableTableHead>
                <ResizableTableHead id="slug" width={cols.widths.slug} onWidthChange={(w) => cols.setWidth("slug", w)} className="text-[#6B6B6B] text-xs">Slug</ResizableTableHead>
                <ResizableTableHead id="date" width={cols.widths.date} onWidthChange={(w) => cols.setWidth("date", w)} className="text-[#6B6B6B] text-xs">Date</ResizableTableHead>
                <ResizableTableHead id="statut" width={cols.widths.statut} onWidthChange={(w) => cols.setWidth("statut", w)} className="text-[#6B6B6B] text-xs">Statut</ResizableTableHead>
                <ResizableTableHead id="actions" width={cols.widths.actions} onWidthChange={(w) => cols.setWidth("actions", w)} className="text-[#6B6B6B] text-xs text-right">Actions</ResizableTableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={7} className="text-center py-12"><Loader2 className="w-5 h-5 animate-spin mx-auto text-[#2F4F4F]" /></TableCell></TableRow>
              ) : articles.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center text-[#6B6B6B]/50 py-12 text-sm">Aucun article. Cliquez sur "Articles piliers" ou "Générer" pour commencer.</TableCell></TableRow>
              ) : articles.map(a => (
                <TableRow key={a.id} className="hover:bg-[#FBF9F5]/50 transition-colors">
                  <TableCell>
                    <button onClick={() => handleToggleFeatured(a)} className="p-1">
                      <Star className={`w-4 h-4 ${a.featured ? "text-amber-500 fill-amber-500" : "text-[#BFBFBF] hover:text-amber-300"}`} />
                    </button>
                  </TableCell>
                  <TableCell>
                    {a.cover_image ? (
                      <div className="w-10 h-10 rounded-md overflow-hidden bg-[#FBF9F5] border border-[#BFBFBF]">
                        <img src={a.cover_image} alt="" className="w-full h-full object-cover" />
                      </div>
                    ) : (
                      <div className="w-10 h-10 rounded-md bg-[#FBF9F5] border border-dashed border-[#BFBFBF] flex items-center justify-center">
                        <ImageIcon className="w-3.5 h-3.5 text-[#BFBFBF]" />
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <p className="font-medium text-[#2F4F4F] text-sm truncate max-w-[300px]">{a.title}</p>
                    {a.is_pillar && <Badge className="bg-amber-100 text-amber-700 border-0 text-[9px] rounded-full mt-0.5">Pilier</Badge>}
                  </TableCell>
                  <TableCell className="text-[#6B6B6B] text-xs font-mono truncate max-w-[150px]">/{a.slug}</TableCell>
                  <TableCell className="text-[#6B6B6B] text-xs">{a.published_at ? new Date(a.published_at).toLocaleDateString("fr-FR") : "—"}</TableCell>
                  <TableCell>
                    <button onClick={() => handleStatusToggle(a)}>
                      {a.status === "published"
                        ? <Badge className="bg-green-100 text-green-700 border-0 text-[10px] rounded-full cursor-pointer">Publié</Badge>
                        : <Badge className="bg-gray-100 text-gray-600 border-0 text-[10px] rounded-full cursor-pointer">Brouillon</Badge>
                      }
                    </button>
                  </TableCell>
                  <TableCell>
                    <div className="flex justify-end gap-0.5">
                      {a.status === "published" && (
                        <Tip label="Voir sur le blog">
                          <a href={`/blog/${a.slug}`} target="_blank" rel="noopener noreferrer">
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-[#6B6B6B] hover:text-[#2F4F4F]"><ExternalLink className="w-3.5 h-3.5" /></Button>
                          </a>
                        </Tip>
                      )}
                      <Tip label={a.cover_image ? "Régénérer l'image" : "Générer l'image"}>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-[#6B6B6B] hover:text-purple-600" onClick={() => handleRegenerateCover(a)} data-testid={`regenerate-cover-${a.slug}`}>
                          {a.cover_image ? <RefreshCw className="w-3.5 h-3.5" /> : <ImageIcon className="w-3.5 h-3.5" />}
                        </Button>
                      </Tip>
                      <Tip label="Modifier">
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-[#6B6B6B] hover:text-[#2F4F4F]" onClick={() => setEditDialog(a)}><Edit2 className="w-3.5 h-3.5" /></Button>
                      </Tip>
                      <Tip label="Supprimer">
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-[#6B6B6B] hover:text-red-500" onClick={() => handleDelete(a)}><Trash2 className="w-3.5 h-3.5" /></Button>
                      </Tip>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {pages > 1 && (
          <div className="flex justify-center gap-2 items-center">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="border-[#BFBFBF] text-[#6B6B6B] text-xs rounded-full">Précédent</Button>
            <span className="text-xs text-[#6B6B6B]">{page} / {pages}</span>
            <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage(p => p + 1)} className="border-[#BFBFBF] text-[#6B6B6B] text-xs rounded-full">Suivant</Button>
          </div>
        )}

        {editDialog && <ArticleEditDialog article={editDialog} onClose={() => setEditDialog(null)} onSuccess={() => { setEditDialog(null); loadArticles(); loadStats(); }} onCoverChange={() => { loadArticles(); loadStats(); }} />}
        {createDialog && <ArticleCreateDialog onClose={() => setCreateDialog(false)} onSuccess={() => { setCreateDialog(false); loadArticles(); loadStats(); }} />}

        <AlertDialog open={pillarConfirmOpen} onOpenChange={setPillarConfirmOpen}>
          <AlertDialogContent className="bg-white border-[#BFBFBF]" data-testid="pillar-confirm-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-[#1F3535]" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                Régénérer les 5 articles piliers ?
              </AlertDialogTitle>
              <AlertDialogDescription className="text-[#4A4A4A] text-sm leading-relaxed">
                Cette action va <strong>supprimer les articles piliers existants</strong> puis régénérer les 5 nouveaux piliers (texte + image de couverture).
                <br /><br />
                La génération prend environ <strong>2 à 5 minutes</strong>. Souhaitez-vous continuer ?
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-full border-[#BFBFBF]" data-testid="pillar-confirm-cancel">Annuler</AlertDialogCancel>
              <AlertDialogAction onClick={confirmAndGeneratePillars} className="bg-[#C46A4A] text-white hover:bg-[#A6553A] rounded-full" data-testid="pillar-confirm-action">
                Oui, régénérer les piliers
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </TooltipProvider>
  );
}


function ArticleEditDialog({ article, onClose, onSuccess, onCoverChange }) {
  const [form, setForm] = useState({ title: article.title || "", meta_title: article.meta_title || "", meta_description: article.meta_description || "", summary: article.summary || "", keywords: (article.keywords || []).join(", ") });
  const [content, setContent] = useState("");
  const [loadingContent, setLoadingContent] = useState(true);
  const [saving, setSaving] = useState(false);
  const [coverUrl, setCoverUrl] = useState(article.cover_image || null);
  const [coverBusy, setCoverBusy] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/admin/blog/articles/${article.id}`);
        setContent(data.content || "");
        setCoverUrl(data.cover_image || null);
      } catch {}
      setLoadingContent(false);
    })();
  }, [article.id]);

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/admin/blog/articles/${article.id}`, { ...form, content, keywords: form.keywords.split(",").map(k => k.trim()).filter(Boolean) });
      toast.success("Article mis à jour");
      onSuccess();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    setSaving(false);
  };

  const handleCoverUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error("Image trop lourde (max 8 Mo)"); return; }
    setCoverBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/admin/blog/articles/${article.id}/upload-cover`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setCoverUrl(`${data.cover_image}?t=${Date.now()}`);
      toast.success("Image uploadée");
      onCoverChange?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur upload"); }
    setCoverBusy(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleCoverDelete = async () => {
    setCoverBusy(true);
    try {
      await api.delete(`/admin/blog/articles/${article.id}/cover`);
      setCoverUrl(null);
      toast.success("Image supprimée");
      onCoverChange?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    setCoverBusy(false);
  };

  const handleCoverRegenerate = async () => {
    setCoverBusy(true);
    try {
      await api.post(`/admin/blog/articles/${article.id}/regenerate-cover`);
      toast.success("Génération lancée — rafraîchissement dans 30s");
      setTimeout(async () => {
        try {
          const { data } = await api.get(`/admin/blog/articles/${article.id}`);
          setCoverUrl(data.cover_image ? `${data.cover_image}?t=${Date.now()}` : null);
          onCoverChange?.();
        } catch {}
        setCoverBusy(false);
      }, 30000);
      return;
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); setCoverBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl border-[#BFBFBF] bg-white max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="text-[#2F4F4F]" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Modifier l'article</DialogTitle></DialogHeader>

        {/* Cover image manager */}
        <div className="space-y-2 bg-[#FBF9F5]/60 rounded-lg p-3 border border-[#BFBFBF]">
          <Label className="text-xs text-[#6B6B6B] font-medium">Image de couverture</Label>
          <div className="flex gap-3 items-start">
            <div className="w-32 h-20 rounded-md overflow-hidden bg-white border border-[#BFBFBF] flex-shrink-0 flex items-center justify-center">
              {coverUrl ? (
                <img src={coverUrl} alt="Cover" className="w-full h-full object-cover" />
              ) : (
                <ImageIcon className="w-6 h-6 text-[#BFBFBF]" />
              )}
            </div>
            <div className="flex-1 space-y-2">
              <div className="flex gap-1.5 flex-wrap">
                <input ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={handleCoverUpload} data-testid="cover-upload-input" />
                <Button type="button" size="sm" variant="outline" disabled={coverBusy} onClick={() => fileInputRef.current?.click()} className="rounded-full text-xs border-[#2F4F4F]/20 text-[#2F4F4F] hover:bg-[#2F4F4F]/5" data-testid="cover-upload-btn">
                  {coverBusy ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Upload className="w-3 h-3 mr-1" />}
                  {coverUrl ? "Remplacer" : "Uploader"}
                </Button>
                <Button type="button" size="sm" variant="outline" disabled={coverBusy} onClick={handleCoverRegenerate} className="rounded-full text-xs border-purple-200 text-purple-600 hover:bg-purple-50" data-testid="cover-regen-btn">
                  {coverBusy ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <RefreshCw className="w-3 h-3 mr-1" />}
                  IA
                </Button>
                {coverUrl && (
                  <Button type="button" size="sm" variant="outline" disabled={coverBusy} onClick={handleCoverDelete} className="rounded-full text-xs border-red-200 text-red-500 hover:bg-red-50" data-testid="cover-delete-btn">
                    <Trash2 className="w-3 h-3 mr-1" /> Supprimer
                  </Button>
                )}
              </div>
              <p className="text-[10px] text-[#999] leading-snug">PNG, JPG ou WebP — max 8 Mo. Format 16:9 recommandé.</p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSave} className="space-y-3">
          <div><Label className="text-xs text-[#6B6B6B]">Titre</Label><Input value={form.title} onChange={e => update("title", e.target.value)} className={inputCls} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs text-[#6B6B6B]">Meta title</Label><Input value={form.meta_title} onChange={e => update("meta_title", e.target.value)} className={inputCls} maxLength={60} /></div>
            <div><Label className="text-xs text-[#6B6B6B]">Mots-clés (virgules)</Label><Input value={form.keywords} onChange={e => update("keywords", e.target.value)} className={inputCls} /></div>
          </div>
          <div><Label className="text-xs text-[#6B6B6B]">Meta description</Label><Input value={form.meta_description} onChange={e => update("meta_description", e.target.value)} className={inputCls} maxLength={155} /></div>
          <div><Label className="text-xs text-[#6B6B6B]">Résumé</Label><Input value={form.summary} onChange={e => update("summary", e.target.value)} className={inputCls} /></div>
          <div>
            <Label className="text-xs text-[#6B6B6B]">Contenu HTML</Label>
            {loadingContent ? <div className="text-center py-4 text-[#6B6B6B]"><Loader2 className="w-4 h-4 animate-spin mx-auto" /></div> : (
              <Textarea value={content} onChange={e => setContent(e.target.value)} className={`${inputCls} min-h-[200px] font-mono text-xs`} />
            )}
          </div>
          <DialogFooter className="gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="rounded-full border-[#BFBFBF] text-[#6B6B6B]">Annuler</Button>
            <Button type="submit" disabled={saving} className="bg-[#2F4F4F] text-white hover:bg-[#1F3535] rounded-full">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enregistrer"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}


function ArticleCreateDialog({ onClose, onSuccess }) {
  const [form, setForm] = useState({ title: "", meta_title: "", meta_description: "", summary: "", keywords: "", status: "draft" });
  const [content, setContent] = useState("");
  const [saving, setSaving] = useState(false);
  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !content.trim()) { toast.error("Titre et contenu requis"); return; }
    setSaving(true);
    try {
      await api.post("/admin/blog/create", { ...form, content, keywords: form.keywords.split(",").map(k => k.trim()).filter(Boolean) });
      toast.success("Article créé");
      onSuccess();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    setSaving(false);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl border-[#BFBFBF] bg-white max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="text-[#2F4F4F] font-montserrat tracking-tight">Nouvel article</DialogTitle></DialogHeader>
        <form onSubmit={handleSave} className="space-y-3">
          <div><Label className="text-xs text-[#6B6B6B]">Titre</Label><Input value={form.title} onChange={e => update("title", e.target.value)} className={inputCls} required placeholder="Mon titre d'article" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs text-[#6B6B6B]">Meta title</Label><Input value={form.meta_title} onChange={e => update("meta_title", e.target.value)} className={inputCls} maxLength={60} /></div>
            <div><Label className="text-xs text-[#6B6B6B]">Mots-clés (virgules)</Label><Input value={form.keywords} onChange={e => update("keywords", e.target.value)} className={inputCls} /></div>
          </div>
          <div><Label className="text-xs text-[#6B6B6B]">Meta description</Label><Input value={form.meta_description} onChange={e => update("meta_description", e.target.value)} className={inputCls} maxLength={155} /></div>
          <div><Label className="text-xs text-[#6B6B6B]">Résumé</Label><Input value={form.summary} onChange={e => update("summary", e.target.value)} className={inputCls} /></div>
          <div>
            <Label className="text-xs text-[#6B6B6B]">Contenu HTML</Label>
            <Textarea value={content} onChange={e => setContent(e.target.value)} className={`${inputCls} min-h-[200px] font-mono text-xs`} placeholder="<h2>Mon sous-titre</h2><p>Mon contenu...</p>" />
          </div>
          <div>
            <Label className="text-xs text-[#6B6B6B]">Statut</Label>
            <Select value={form.status} onValueChange={v => update("status", v)}>
              <SelectTrigger className="h-9 text-sm border-[#BFBFBF] rounded-lg"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white border-[#BFBFBF]">
                <SelectItem value="draft">Brouillon</SelectItem>
                <SelectItem value="published">Publié</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter className="gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="rounded-full border-[#BFBFBF] text-[#6B6B6B]">Annuler</Button>
            <Button type="submit" disabled={saving} className="bg-[#2F4F4F] text-white hover:bg-[#1F3535] rounded-full">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Créer"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
